/*
 * Iter XX.43 — Unified Partner administration for the founder dashboard.
 *
 * Sits alongside the legacy FounderPartnersTab. Shows KPI tiles,
 * partner table with actions (verify / pause / suspend / terminate /
 * rename username / override attribution), and full audit trail.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export function FounderUnifiedPartnersSection() {
  const [overview, setOverview] = useState(null);
  const [partners, setPartners] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ov, list] = await Promise.all([
        api.get("/admin/partners2/overview"),
        api.get("/admin/partners2", { params: {
          status: statusFilter || undefined,
          partner_type: typeFilter || undefined,
        }}),
      ]);
      setOverview(ov.data); setPartners(list.data.partners || []); setErr(null);
    } catch (e) { setErr(e?.response?.data?.detail || "load_failed"); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  async function runMigration() {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/partners2/migrate");
      const s = data.migration || {};
      toast.success(`Migration: ${s.created} created, ${s.reused} reused, ${s.attribution_backfilled} attributions back-filled`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "migration_failed"); }
    finally { setRunning(false); }
  }

  async function patchPartner(partnerId, body) {
    try {
      const { data } = await api.patch(`/admin/partners2/${partnerId}`, body);
      toast.success("Updated");
      setPartners(ps => ps.map(p => p.partner_id === partnerId ? { ...p, ...data.partner } : p));
    } catch (e) { toast.error(e?.response?.data?.detail || "update_failed"); }
  }

  async function loadAudit(partnerId) {
    try {
      const { data } = await api.get(`/admin/partners2/${partnerId}/audit`);
      setAudit(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "audit_failed"); }
  }

  async function overrideAttribution(partnerId) {
    const userId = window.prompt("User ID to reattribute?");
    if (!userId) return;
    const reason = window.prompt("Reason for override?") || "";
    try {
      await api.post(`/admin/partners2/${partnerId}/override-attribution`,
        { user_id: userId, partner_id: partnerId, reason });
      toast.success("Attribution overridden");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "override_failed"); }
  }

  return (
    <section data-testid="section-founder-unified-partners" className="space-y-4">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <p className="label-eyebrow">unified partners · Iter XX.43</p>
          <p className="text-xs text-neutral-500 mt-0.5">
            academies · coaches · creators · influencers · clubs · schools · leagues
          </p>
        </div>
        <button
          onClick={runMigration}
          disabled={running}
          className="text-xs px-3 py-1.5 rounded border border-neutral-700 hover:border-emerald-500 text-neutral-300 hover:text-emerald-300 disabled:opacity-40"
          data-testid="unified-partners-run-migration">
          {running ? "Running…" : "Migrate legacy academies"}
        </button>
      </div>

      {err && <div className="surface-1 p-4 text-red-400 text-sm">{err}</div>}

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total partners" value={overview.totals?.total} testid="unified-tile-total" />
          <Tile label="Active" value={overview.totals?.active} testid="unified-tile-active" />
          <Tile label="Verified" value={overview.totals?.verified} testid="unified-tile-verified" />
          <Tile label="Suspended" value={overview.totals?.suspended} testid="unified-tile-suspended" />
          <Tile label="Profile visits" value={overview.profile_visits} testid="unified-tile-visits" />
          <Tile label="Referral clicks (30d)" value={overview.referral_clicks_30d} testid="unified-tile-clicks-30d" />
          <Tile label="Attributed players" value={overview.attributed_players} testid="unified-tile-attributed" />
          <Tile label="Plus subscribers" value={overview.plus_subscribers} testid="unified-tile-plus-subs" />
        </div>
      )}

      <div className="flex gap-2">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-neutral-800 bg-neutral-950 text-neutral-300"
                data-testid="unified-filter-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="suspended">Suspended</option>
          <option value="draft">Draft</option>
          <option value="terminated">Terminated</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-neutral-800 bg-neutral-950 text-neutral-300"
                data-testid="unified-filter-type">
          <option value="">All types</option>
          <option value="academy">Academy</option>
          <option value="coach">Coach</option>
          <option value="creator">Creator</option>
          <option value="influencer">Influencer</option>
          <option value="club">Club</option>
          <option value="school">School</option>
          <option value="league">League</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="overflow-x-auto surface-1">
        <table className="w-full text-sm" data-testid="unified-partners-table">
          <thead className="text-xs text-neutral-500">
            <tr className="border-b border-neutral-800">
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">@user</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Verify</th>
              <th className="text-right px-3 py-2">Attr</th>
              <th className="text-right px-3 py-2">Plus</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map(p => (
              <tr key={p.partner_id} className="border-b border-neutral-900 hover:bg-neutral-900/40" data-testid={`unified-row-${p.username}`}>
                <td className="px-3 py-2 text-neutral-200">{p.display_name}</td>
                <td className="px-3 py-2 text-emerald-300"><a href={`/@${p.username}`} target="_blank" rel="noopener noreferrer">@{p.username}</a></td>
                <td className="px-3 py-2 text-neutral-400 capitalize">{p.partner_type}</td>
                <td className="px-3 py-2">
                  <StatusPill status={p.status} />
                </td>
                <td className="px-3 py-2 text-neutral-400 capitalize">{p.verification_status}</td>
                <td className="px-3 py-2 text-right text-neutral-300">{p.metrics?.attributed_players ?? 0}</td>
                <td className="px-3 py-2 text-right text-neutral-300">{p.metrics?.plus_subscribers ?? 0}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1 text-xs">
                    {p.verification_status !== "verified" &&
                      <ActionBtn onClick={() => patchPartner(p.partner_id, { verification_status: "verified" })} testid={`verify-${p.username}`}>Verify</ActionBtn>}
                    {p.verification_status === "verified" &&
                      <ActionBtn onClick={() => patchPartner(p.partner_id, { verification_status: "unverified" })} testid={`unverify-${p.username}`}>Unverify</ActionBtn>}
                    {p.status === "active" &&
                      <ActionBtn onClick={() => patchPartner(p.partner_id, { status: "paused" })} testid={`pause-${p.username}`}>Pause</ActionBtn>}
                    {p.status === "paused" &&
                      <ActionBtn onClick={() => patchPartner(p.partner_id, { status: "active" })} testid={`unpause-${p.username}`}>Unpause</ActionBtn>}
                    {p.status !== "suspended" &&
                      <ActionBtn onClick={() => patchPartner(p.partner_id, { status: "suspended", note: "founder_action" })} testid={`suspend-${p.username}`}>Suspend</ActionBtn>}
                    <ActionBtn onClick={() => overrideAttribution(p.partner_id)} testid={`override-${p.username}`}>Override attr</ActionBtn>
                    <ActionBtn onClick={() => loadAudit(p.partner_id)} testid={`audit-${p.username}`}>Audit</ActionBtn>
                  </div>
                </td>
              </tr>
            ))}
            {!partners.length && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-500 text-sm">No partners yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {audit && (
        <div className="surface-1 p-4 mt-4" data-testid="unified-audit-panel">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm text-neutral-300">Audit trail — @{audit.partner?.username}</div>
            <button onClick={() => setAudit(null)} className="text-xs text-neutral-500">Close</button>
          </div>
          <div className="text-xs text-neutral-400 space-y-1 max-h-64 overflow-y-auto">
            {(audit.audit_log || []).map(row => (
              <div key={row.log_id} className="flex justify-between border-b border-neutral-900 pb-1">
                <span>{row.action}</span>
                <span className="text-neutral-600">{row.at?.slice(0, 19)}</span>
              </div>
            ))}
            {!audit.audit_log?.length && <div className="text-neutral-600">No entries.</div>}
          </div>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="surface-1 p-3" data-testid={testid}>
      <div className="text-2xl text-emerald-300">{value ?? "—"}</div>
      <div className="text-xs text-neutral-500 mt-0.5">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "active" ? "bg-emerald-500/10 text-emerald-400" :
              status === "paused" ? "bg-amber-500/10 text-amber-400" :
              status === "suspended" ? "bg-red-500/10 text-red-400" :
              "bg-neutral-800 text-neutral-400";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}

function ActionBtn({ onClick, children, testid }) {
  return (
    <button onClick={onClick}
            className="px-2 py-0.5 rounded border border-neutral-800 hover:border-emerald-600 text-neutral-400 hover:text-emerald-300"
            data-testid={testid}>{children}</button>
  );
}
